

import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Task, TaskStatus } from "@/stores/taskStore";
import { TaskCard } from "./TaskCard";
import { cn } from "@/lib/utils";
import { MoreHorizontal, Plus } from "lucide-react";

interface KanbanColumnProps {
  id: TaskStatus;
  title: string;
  tasks: Task[];
  onAddTask?: () => void;
  onTaskClick?: (task: Task) => void;
}

const columnColors: Record<TaskStatus, string> = {
  PENDING: "bg-zinc-100 dark:bg-zinc-800",
  IN_PROGRESS: "bg-blue-50 dark:bg-blue-950/30",
  IN_REVIEW: "bg-amber-50 dark:bg-amber-950/30",
  COMPLETED: "bg-green-50 dark:bg-green-950/30",
};

export function KanbanColumn({
  id,
  title,
  tasks,
  onAddTask,
  onTaskClick,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div className="flex flex-col w-72 min-w-72 bg-muted/30 rounded-lg">
      <div className={cn("p-3 rounded-t-lg border-b", columnColors[id])}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-sm">{title}</h3>
            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {tasks.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onAddTask}
              className="p-1 hover:bg-muted rounded"
              title="Agregar tarea"
            >
              <Plus className="w-4 h-4 text-muted-foreground" />
            </button>
            <button className="p-1 hover:bg-muted rounded">
              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={setNodeRef}
        data-column-id={id}
        className={cn(
          "flex-1 p-2 space-y-2 overflow-y-auto min-h-48 transition-colors",
          isOver && "bg-muted/50 ring-2 ring-primary ring-inset"
        )}
      >
        <SortableContext
          items={tasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => onTaskClick?.(task)}
            />
          ))}
        </SortableContext>

        {tasks.length === 0 && !isOver && (
          <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
            Sin tareas
          </div>
        )}
      </div>
    </div>
  );
}
